import { inject, Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { CommonConfirmDialog } from "../components/common-confirm-dialog/common-confirm-dialog.model";
import { debounceTime, fromEvent, merge, Subscription, take } from "rxjs";
import { AuthService } from "../../features/authenticate/auth-service";
import { SessionDialogComponent } from "../components/session-dialog-component/session-dialog-component";
import { AuthTokenStore } from "../stores/auth-token-store";

@Injectable({
  providedIn: "root"
})
export class SessionTimeoutService {
  private logoutTimer!: ReturnType<typeof setTimeout>;
  private warningTimer!: ReturnType<typeof setTimeout>;
  private activitySubscription!: Subscription;
  private authService = inject(AuthService);
  private authTokenStore = inject(AuthTokenStore);
  private dialog = inject(MatDialog);
  private dialogOpen = false;
  private sessionDialogRef: MatDialogRef<SessionDialogComponent> | null = null;

  startWatching(): void {
    const token = this.authTokenStore.getAccessToken();
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }
    if (!token) {
      this.stopWatching();
      return;
    }
    this.monitorUserActivity();
    this.resetTimers();
  }
  private monitorUserActivity(): void {
    const activityEvent$ = merge(
      fromEvent(document, "mousemove"),
      fromEvent(document, "keydown"),
      fromEvent(document, "click"),
      fromEvent(document, "scroll"),
      fromEvent(document, "touchstart")
    );
    const visibilityEvent$ = fromEvent(document, "visibilitychange");
    const windowEvent$ = merge(fromEvent(document, "blur"), fromEvent(document, "focus"));
    this.activitySubscription = merge(activityEvent$, visibilityEvent$, windowEvent$)
      .pipe(debounceTime(100))
      .subscribe(() => {
        if (document.visibilityState === "hidden") {
          return;
        }
        this.resetTimers();
      });
  }
  private resetTimers(): void {
    clearTimeout(this.warningTimer);
    clearTimeout(this.logoutTimer);
    this.warningTimer = setTimeout(
      () => {
        this.openWarningDialog();
      },
      14 * 60 * 1000
    );
    this.logoutTimer = setTimeout(
      () => {
        this.logout();
      },
      15 * 60 * 1000
    );
  }
  private openWarningDialog(): void {
    if (this.dialogOpen) return;
    this.dialogOpen = true;

    const dialogData: CommonConfirmDialog = {
      title: "SESSIONPOPUP.TITLE",
      cancelButtonText: "SESSIONPOPUP.SIGN_OUT_BUTTON_TEXT",
      confirmButtonText: "SESSIONPOPUP.ON_STAY_BUTTON_TEXT",
      message: "SESSIONPOPUP.MESSAGE"
    };
    this.sessionDialogRef = this.dialog.open(SessionDialogComponent, {
      width: "410px",
      data: dialogData,
      disableClose: true
    });
    this.sessionDialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: boolean | undefined) => {
        this.dialogOpen = false;
        this.sessionDialogRef = null;
        if (!this.authTokenStore.getAccessToken()) {
          return;
        }
        if (result === true) {
          this.resetTimers();
        } else if (result === false) {
          this.logout();
        }
      });
  }
  stopWatching(): void {
    clearTimeout(this.warningTimer);
    clearTimeout(this.logoutTimer);
    this.activitySubscription?.unsubscribe();
    this.sessionDialogRef?.close();
    this.sessionDialogRef = null;
    this.dialogOpen = false;
  }
  private logout(): void {
    this.stopWatching();
    this.authService.logout();
  }
}
